import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Boxes } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Err } from '@/components/Err'
import { auth } from '@/api/endpoints'
import type { OAuthProvider, User } from '@/api/types'
import { PROVIDER_LABEL } from '@/domain/words'

// OAuth 콜백이 실패로 돌아올 때의 문장(F-34). `?oauth=` 의 값마다 하나.
const OAUTH_FAILED: Record<string, string> = {
  state: '로그인을 다시 시도하세요.',
  unverified: '이 계정의 이메일이 확인되지 않아 로그인할 수 없습니다.',
  disabled: '비활성화된 계정입니다.',
  closed: '지금은 계정을 신청할 수 없습니다.',
  failed: '로그인에 실패했습니다.',
}

// 로그인의 401 은 없는 아이디·틀린 비밀번호·비활성 계정을 가르지 않는 한 답이다 — 세션 만료의 `로그인이 필요합니다` 가 아니다.
const LOGIN_CODES = { unauthorized: '아이디 또는 비밀번호가 맞지 않습니다.' } as const

export default function Login({ onIn }: { onIn: (u: User) => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  // 계정 신청은 메일을 보낼 수 있고 최고 관리자가 닫지 않았을 때만 열린다 — 서버가 말한다.
  const [signup, setSignup] = useState(false)
  // 비밀번호 찾기는 메일만 본다 — 회원가입을 닫아도 계정이 있는 사람의 길은 남는다.
  const [reset, setReset] = useState(false)
  const [providers, setProviders] = useState<OAuthProvider[]>([])
  const [params] = useSearchParams()
  const oauthFailed = params.get('oauth')
  useEffect(() => {
    let live = true
    auth.meta().then((m) => { if (live) { setSignup(Boolean(m.signup)); setReset(Boolean(m.password_reset)); setProviders(m.oauth ?? []) } }).catch(() => undefined)
    return () => { live = false }
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const s = await auth.login(username, password)
      setPassword('') // 비밀번호는 화면에도 남기지 않는다
      onIn(s.user)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid h-full place-items-center">
      <form onSubmit={submit} className="grid w-full max-w-72 gap-3 px-4">
        <div className="mb-2 flex items-center gap-2.5">
          <span className="flex size-9 items-center justify-center rounded-lg bg-brand text-white">
            <Boxes className="size-4.5" />
          </span>
          <span className="grid leading-tight">
            <span className="font-semibold tracking-tight">Leorca Cluster Control System</span>
            <span className="text-[11px] text-muted-foreground">레오르카 클러스터 관제 시스템 · LCCS</span>
          </span>
        </div>
        {/* 아이디든 이메일이든 한 칸이다 — 아이디에 `@` 가 쓰일 수 없어 어느 쪽인지 서버가 가린다(F-78). */}
        <Input
          placeholder="아이디 또는 이메일"
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <Input
          type="password"
          placeholder="비밀번호"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button
          type="submit"
          disabled={busy || !username || !password}
          className="bg-brand hover:bg-brand-hover"
        >
          로그인
        </Button>
        <Err error={error} codes={LOGIN_CODES} />
        {oauthFailed && OAUTH_FAILED[oauthFailed] ? <p role="alert" className="text-[12px] text-danger-ink">{OAUTH_FAILED[oauthFailed]}</p> : null}
        {providers.map((p) => (
          <Button key={p} asChild variant="outline">
            <a href={`/api/v1/auth/oauth/${p}/start`}>{PROVIDER_LABEL[p]} 로 로그인</a>
          </Button>
        ))}
        {signup || reset ? (
          <div className="flex justify-center gap-3 text-[12px] text-muted-foreground">
            {signup ? <Link to="/signup" className="hover:text-foreground hover:underline">계정 신청</Link> : null}
            {reset ? <Link to="/reset" className="hover:text-foreground hover:underline">비밀번호 찾기</Link> : null}
          </div>
        ) : null}
      </form>
    </div>
  )
}
